'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import { wagamourV2Button, type WagamourV2ButtonVariant } from '@/lib/design/v2Button'

export interface WagamourV2ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: WagamourV2ButtonVariant
  /** Full-width block button (common in modals). */
  fullWidth?: boolean
}

const sizeClasses = {
  default: 'min-h-[44px] px-5 py-2.5 text-sm',
  sm: 'min-h-[36px] px-4 py-2 text-xs',
  lg: 'min-h-[48px] px-6 py-3 text-base',
} as const

export type WagamourV2ButtonSize = keyof typeof sizeClasses

export interface WagamourV2ButtonPropsWithSize extends WagamourV2ButtonProps {
  size?: WagamourV2ButtonSize
}

/**
 * Canonical V2 button — sunrise primary, warm glass secondary, semantic destructive.
 */
export function WagamourV2Button({
  variant = 'primary',
  size = 'default',
  fullWidth = false,
  className,
  style,
  disabled,
  type = 'button',
  ...props
}: WagamourV2ButtonPropsWithSize) {
  const tokens = wagamourV2Button[variant]

  return (
    <button
      type={type}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-2xl font-semibold',
        'transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(251,191,36,0.55)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
        'disabled:pointer-events-none disabled:opacity-50',
        variant === 'primary' && 'hover:brightness-[1.04] active:brightness-[0.96]',
        variant !== 'primary' && 'hover:brightness-[1.06] active:brightness-[0.94]',
        fullWidth && 'w-full',
        sizeClasses[size],
        className
      )}
      style={{
        background: tokens.background,
        color: tokens.color,
        border: `1px solid ${tokens.border}`,
        boxShadow: tokens.boxShadow,
        backdropFilter: tokens.backdropFilter,
        WebkitBackdropFilter: tokens.WebkitBackdropFilter,
        ...style,
      }}
      {...props}
    />
  )
}
